import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';

/**
 * Sticky red pill at the top-center: "⚠️ Beware of unauthorised sellers".
 * Click → modal explaining pisatarios / invaders and what to verify before
 * paying for any plot.
 */
export function WarningBanner() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Close on Esc + click outside the modal box.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {/* Sticky banner across the top — pushes the menu and help buttons down. */}
      <button
        onClick={() => setOpen(true)}
        title={t('warning_tap')}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 36,
          padding: '0 14px',
          background: 'rgba(220, 38, 38, 0.95)',
          color: 'white',
          border: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 0,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          cursor: 'pointer',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          boxShadow: '0 2px 10px rgba(220, 38, 38, 0.35)',
          zIndex: 6,
        }}
      >
        <span>⚠️</span>
        <span>{t('warning_header')}</span>
      </button>

      {/* Modal */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 16,
          }}
        >
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 520,
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#1f2937',
              color: 'white',
              borderRadius: 12,
              padding: '22px 24px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
              border: '1px solid rgba(220, 38, 38, 0.5)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div style={{ fontSize: 26, lineHeight: 1 }}>⚠️</div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 17,
                  fontWeight: 700,
                  flex: 1,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                }}
              >
                {t('warning_header')}
              </h2>
            </div>

            <Paragraph text={t('warning_p1')} />
            <Paragraph text={t('warning_p2')} />
            <Paragraph text={t('warning_p3')} />
            <Paragraph text={t('warning_p4')} accent />

            <button
              onClick={() => setOpen(false)}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '10px 14px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {t('warning_got_it')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Render text with `*word*` segments converted to bold red emphasis. */
function Paragraph({ text, accent = false }: { text: string; accent?: boolean }) {
  const parts = text.split(/(\*[^*]+\*)/g).map((seg, i) => {
    if (seg.startsWith('*') && seg.endsWith('*')) {
      return (
        <strong key={i} style={{ color: '#fca5a5' }}>
          {seg.slice(1, -1)}
        </strong>
      );
    }
    return <span key={i}>{seg}</span>;
  });
  return (
    <p
      style={{
        margin: '8px 0',
        fontWeight: accent ? 600 : 400,
        color: accent ? '#fef3c7' : 'rgba(255,255,255,0.9)',
      }}
    >
      {parts}
    </p>
  );
}
